import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import { produce } from 'immer';
import { SButton, SDrawer, SModal } from '@chargebee/sting-react';
import {
  EditRuleContext,
  EditRuleProvider,
} from '@bbkAdminComponents/experiences/audiences/edit-rule/edit-rule-context';
import { OptimizationActionForm } from '@bbkAdminComponents/optimizer/OptimizationActionForm';
import { useOptimizationEditor } from '@bbkAdminComponents/optimizer/OptimizationEditorContext';
import { OptimizationEditorHelper } from '@bbkAdminComponents/optimizer/OptimizationEditorHelper';
import { selectAppId } from '@bbkAdminRedux/app/selectors';
import { AppFieldType } from '@bbkAdminRedux/data-management/reducers';
import { useSelectBillingIntegration } from '@bbkAdminRedux/rtkq/integrations.slice';
import {
  OptimizationSliceHelper,
  type OptimizationAction,
} from '@bbkAdminRedux/rtkq/optimizations.slice';
import { AuthorizedPaths } from '@bbkAdminRouter/router-paths';
import { useUrl } from '@bbkAdminUtils/nav';
import { useHistory, useParams } from '@bbkShared/react-router';

const OptimizationActionComponent: React.FC<{
  action: OptimizationAction;
  onClose: () => unknown;
  onSave: (updated: OptimizationAction) => unknown;
}> = ({ action: initAction, onClose, onSave }) => {
  const history = useHistory();
  const createUrl = useUrl();
  const params = useParams<{
    optId: string;
    step: string;
    actionIdx?: string;
  }>();

  const [action, setAction] = useState(initAction);
  const [showDiscardModal, setShowDiscardModal] = useState(false);
  const { rule } = useContext(EditRuleContext);

  useEffect(() => {
    setAction((prev) =>
      produce(prev, (draft) => {
        draft.condition = rule.audience;
      })
    );
  }, [rule]);

  // Check if the form has unsaved changes (using JSON.stringify for consistency with OptimizationEditorPage)
  const isDirty = useMemo(
    () => JSON.stringify(action) !== JSON.stringify(initAction),
    [action, initAction]
  );

  const handleClose = useCallback(() => {
    history.replace(
      createUrl(AuthorizedPaths.pricingOptimizationEdit, {
        optId: params.optId,
        step: params.step,
      })
    );
  }, [history, createUrl, params.optId, params.step]);

  const handleAttemptClose = useCallback(() => {
    if (isDirty) {
      setShowDiscardModal(true);
    } else {
      handleClose();
    }
  }, [isDirty, handleClose]);

  const handleDiscardConfirm = useCallback(() => {
    setShowDiscardModal(false);
    handleClose();
  }, [handleClose]);

  return (
    <>
      <SDrawer
        modal
        onOpenChange={(open) => {
          if (!open) {
            handleAttemptClose();
          }
        }}
        open
      >
        <SDrawer.Content
          className="!tw-max-w-[100vw] [&_*[class*='option']]:!tw-whitespace-normal [&_*[class*='option']]:!tw-break-all [&_*[class*='singleValue']]:!tw-whitespace-normal [&_*[class*='singleValue']]:!tw-break-all"
          height="full"
          placement="right"
          size="wide"
        >
          <SDrawer.Header showCloseIcon shadow>
            <SDrawer.Title>New Rule</SDrawer.Title>
            <SDrawer.Description />
          </SDrawer.Header>
          <OptimizationActionForm value={action} onChange={setAction} />
          <SDrawer.Footer>
            <SButton variant="neutral" onClick={handleAttemptClose}>
              Cancel
            </SButton>
            <SButton
              variant="primary"
              onClick={() => onSave(action)}
              disabled={
                !OptimizationEditorHelper.areActionsComplete({
                  actions: [action],
                })
              }
            >
              Save
            </SButton>
          </SDrawer.Footer>
        </SDrawer.Content>
      </SDrawer>

      <SModal open={showDiscardModal} onOpenChange={setShowDiscardModal}>
        <SModal.Content size="small">
          <SModal.Header showCloseButton>
            <SModal.Title>Close Rule Editor?</SModal.Title>
            <SModal.Description>
              You have unsaved changes. Are you sure you want to close without
              saving?
            </SModal.Description>
          </SModal.Header>
          <SModal.Footer>
            <SButton
              variant="neutral"
              onClick={() => setShowDiscardModal(false)}
            >
              Keep Editing
            </SButton>
            <SButton variant="danger" onClick={handleDiscardConfirm}>
              Close Without Saving
            </SButton>
          </SModal.Footer>
        </SModal.Content>
      </SModal>
    </>
  );
};

export const OptimizationActionDrawer = () => {
  const history = useHistory();
  const createUrl = useUrl();
  const appKey = useSelector(selectAppId);
  const billingIntegration = useSelectBillingIntegration({ appKey });
  const { optimization, setOptimization } = useOptimizationEditor();
  const params = useParams<{ actionIdx: string }>();
  const actionIdx = parseInt(params.actionIdx, 10);
  const action = useMemo(() => {
    const actions = optimization.actions ?? [];
    return actions[actionIdx] ?? OptimizationSliceHelper.createNewAction();
  }, [optimization.actions, actionIdx]);

  const onClose = useCallback(() => {
    history.replace(
      createUrl(AuthorizedPaths.pricingOptimizationEdit, {
        ...params,
      })
    );
  }, [createUrl, history, params]);

  const updateAction = useCallback(
    (updated: OptimizationAction) => {
      setOptimization((prev) =>
        produce(prev, (draft) => {
          draft.actions ??= [];
          draft.actions[actionIdx] = updated;
        })
      );
      onClose();
    },
    [actionIdx, onClose, setOptimization]
  );

  const properties = useMemo(
    () => [
      {
        name: 'Subscription Plan',
        val: 'subscription_plan',
        type: AppFieldType.text,
        is_lov: true,
        field_name: 'subscription.plan_id',
        integration_id: billingIntegration?.id,
      },
      {
        name: 'Subscription Quantity',
        val: 'subscription_qty',
        type: AppFieldType.integer,
      },
    ],
    [billingIntegration?.id]
  );

  return action ? (
    <EditRuleProvider
      properties={properties}
      rule={{
        // need only `audience` property
        display_name: '',
        client_formatted_rule: '',
        active: false,
        audience: action.condition ?? OptimizationSliceHelper.createNewAction(),
      }}
    >
      <OptimizationActionComponent
        action={action}
        onSave={updateAction}
        onClose={onClose}
      />
    </EditRuleProvider>
  ) : undefined;
};
